import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DigcompCompetenceRows } from '@/components/lms/digcomp/DigcompCompetenceRows';

export interface LessonDigcompInfoProps {
  lesson: {
    id: string;
    title: string;
    description?: string | null;
    transcript?: string | null;
    content?: Record<string, unknown> | null;
  };
  moduleId: string;
  brandId: string;
}

function getTextContent(content: Record<string, unknown> | null | undefined): string | undefined {
  if (!content) return undefined;
  const text = content.text_content || content.textContent;
  return typeof text === 'string' ? text : undefined;
}

export function LessonDigcompInfo({ lesson, moduleId, brandId }: LessonDigcompInfoProps) {
  const { data: moduleData } = useQuery({
    queryKey: ['module-course-id', moduleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('os_lms_modules')
        .select('course_id')
        .eq('id', moduleId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!moduleId,
  });

  const courseId = moduleData?.course_id;

  if (!courseId) return null;

  return (
    <DigcompCompetenceRows
      scope={{ courseId, lessonId: lesson.id }}
      brandId={brandId}
      entityType="lesson"
      content={{
        title: lesson.title,
        description: lesson.description ?? undefined,
        transcript: lesson.transcript ?? undefined,
        text_content: getTextContent(lesson.content),
      }}
      description="Link this lesson to digital competences from the DigComp 3.0 framework. Multiple competences with weights are supported."
      queryKeysToInvalidate={['course-competences', courseId, 'digcomp-linked-content', 'admin-course-details', 'admin-lessons']}
    />
  );
}
